import React from 'react';

/**
 * Stateless pieces of the credential sheet: icons, the Bitwarden proof /
 * ledger disclosure, the in-modal success screen and the drop-task
 * confirm. Styled by secure-credential-modal.scss.
 */

export const ShieldIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    aria-hidden='true'
  >
    <path
      d='M12 2.6 20.2 5.9v6.4c0 5.3-3.4 9-8.2 10.6-4.8-1.6-8.2-5.3-8.2-10.6V5.9L12 2.6Z'
      fill='currentColor'
    />
  </svg>
);

export const ChevronIcon = () => (
  <svg
    className='chev'
    width='15'
    height='15'
    viewBox='0 0 24 24'
    fill='none'
    aria-hidden='true'
  >
    <path
      d='m7 9.5 5 5 5-5'
      stroke='currentColor'
      strokeWidth='1.9'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

export const CloseIcon = () => (
  <svg
    width='19'
    height='19'
    viewBox='0 0 24 24'
    fill='none'
    aria-hidden='true'
  >
    <path
      d='m7 7 10 10M17 7 7 17'
      stroke='currentColor'
      strokeWidth='1.9'
      strokeLinecap='round'
    />
  </svg>
);

const TickIcon = () => (
  <svg
    width='12'
    height='12'
    viewBox='0 0 24 24'
    fill='none'
    aria-hidden='true'
  >
    <path
      d='m5 12.5 4.2 4.2L19 7'
      stroke='currentColor'
      strokeWidth='3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

/** "wwe.com" → "W"; "WWE membership" → "W". */
export const glyphFor = (text: string): string => {
  const m = text.replace(/^https?:\/\//i, '').match(/[a-z0-9]/i);
  return m ? m[0].toUpperCase() : '•';
};

/** Strip scheme / path so "https://www.wwe.com/login" reads as "wwe.com". */
export const displayHost = (site: string): string =>
  site
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/[/?#].*$/, '');

const HOSTNAME_RE =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;

/**
 * Favicon for a fixed site, or null when `site` isn't a plain hostname.
 * Only the host ever reaches the URL — never a credential value.
 */
export const faviconUrlFor = (site: string): string | null => {
  const host = displayHost(site).split(':')[0].toLowerCase();
  return HOSTNAME_RE.test(host) ? `https://${host}/favicon.ico` : null;
};

interface SiteGlyphProps {
  /** Fixed website / service. Omit to show the shield. */
  site?: string;
}

/**
 * The square site tile: the site's favicon when it loads, otherwise the
 * inverse initial; the shield when there's no fixed site.
 */
export const SiteGlyph: React.FC<SiteGlyphProps> = ({ site }) => {
  const url = site ? faviconUrlFor(site) : null;
  const [failedUrl, setFailedUrl] = React.useState<string | null>(null);
  const [loadedUrl, setLoadedUrl] = React.useState<string | null>(null);
  const showImage = url !== null && failedUrl !== url;
  const hasFavicon = url !== null && loadedUrl === url;

  if (!site) {
    return (
      <span className='site-glyph site-glyph--shield' aria-hidden='true'>
        <ShieldIcon size={17} />
      </span>
    );
  }

  return (
    <span
      className={`site-glyph${hasFavicon ? ' site-glyph--favicon' : ''}`}
      aria-hidden='true'
    >
      {showImage && (
        <img
          src={url}
          alt=''
          referrerPolicy='no-referrer'
          onLoad={() => setLoadedUrl(url)}
          onError={() => setFailedUrl(url)}
        />
      )}
      {!hasFavicon && glyphFor(displayHost(site) || site)}
    </span>
  );
};

interface VaultProofProps {
  open: boolean;
  onToggle: () => void;
  /** What ends up in Bitwarden's vault, e.g. "Username and password". */
  vaultedWhat: string;
}

export const VaultProof: React.FC<VaultProofProps> = ({
  open,
  onToggle,
  vaultedWhat,
}) => (
  <div className='proof'>
    <button
      className='proof-toggle'
      type='button'
      aria-expanded={open}
      onClick={onToggle}
    >
      <span className='proof-shield'>
        <ShieldIcon />
      </span>
      <span className='proof-text'>
        <b>Bitwarden, the password manager</b>
        <i>Holds it — your secretary never sees it</i>
      </span>
      <ChevronIcon />
    </button>
    {open && (
      <div className='proof-detail'>
        <p>
          Bitwarden is an independent password manager. The values go into its
          encrypted vault, not our database.
        </p>
        <dl>
          <div className='ledger-row'>
            <dt>Bitwarden’s vault</dt>
            <dd className='blue'>{vaultedWhat}</dd>
          </div>
          <div className='ledger-row'>
            <dt>Dream’s database</dt>
            <dd>A label and a reference</dd>
          </div>
          <div className='ledger-row'>
            <dt>Your AI Secretary</dt>
            <dd className='nil'>Never sees it</dd>
          </div>
        </dl>
        <p className='proof-foot'>
          Every access is logged. Delete it any time from Memories.
        </p>
      </div>
    )}
  </div>
);

interface VaultSavedProps {
  isUpdate: boolean;
  inTask: boolean;
  /** Capitalised assistant name for the sentence start. */
  assistant: string;
  onDone: () => void;
}

export const VaultSaved: React.FC<VaultSavedProps> = ({
  isUpdate,
  inTask,
  assistant,
  onDone,
}) => (
  <div className='done' role='status'>
    <div className='done-glyph' aria-hidden='true'>
      <ShieldIcon size={34} />
      <span className='done-tick'>
        <TickIcon />
      </span>
    </div>
    <h2>{isUpdate ? 'Updated in the vault.' : 'In the vault.'}</h2>
    <p>
      {inTask
        ? `${assistant} can finish up without ever seeing it.`
        : 'Your secretary can use it without ever seeing it.'}
    </p>
    <button className='save' type='button' onClick={onDone} autoFocus>
      {inTask ? 'Back to the task' : 'Done'}
    </button>
  </div>
);

interface DropTaskConfirmProps {
  id: string;
  assistant: string;
  taskTitle?: string;
  onKeep: () => void;
  onDrop: () => void;
}

export const DropTaskConfirm: React.FC<DropTaskConfirmProps> = ({
  id,
  assistant,
  taskTitle,
  onKeep,
  onDrop,
}) => (
  <div className='confirm'>
    <div
      className='confirm-card'
      role='alertdialog'
      aria-modal='true'
      aria-labelledby={id}
    >
      <h3 id={id}>Drop the task?</h3>
      <p>
        Nothing is saved, and {assistant} stops work on{' '}
        {taskTitle || 'this task'}.
      </p>
      <div className='confirm-row'>
        <button className='keep' type='button' onClick={onKeep} autoFocus>
          Keep it
        </button>
        <button className='yes' type='button' onClick={onDrop}>
          Drop task
        </button>
      </div>
    </div>
  </div>
);
